import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  Store,
  X,
  ShoppingBag,
  List,
  Navigation,
  Building2,
  Music,
  Utensils,
  Clock,
  Search,
  Calendar,
  Settings,
  Grid3x3,
  Table2,
  Sofa,
  Waves,
} from 'lucide-react';
import { getData, eventLogger } from '../utils/auth';
import AreaLineChart from './AreaLineChart';
import { getLocalCurrentDateTime, useDateHandler, DAYSOFWEEK } from '../utils/dates';
import { calculateDistance, formatDistance } from '../utils/distance';
import { Location } from '../types';
import { LucideIcon } from 'lucide-react';
import SearchDropdown from './SearchDropdown';
import DayOfWeekSelector from './DayOfWeekSelector';
import {
  BRANDS,
  BRAND_ICONS,
  SUPERMARKET_BRANDS,
  ELECTRONICS_BRANDS,
  LOCATION_ONLY_BRANDS,
  type Brand,
  normalizeBrandName,
  matchesBrand,
} from '../utils/brands';

const LocationComparer2 = ({
  shops,
  handleClose,
  handleSearchSubmission,
  allLocations,
  userLocation,
  onRequestLocation,
  isLocating = false,
  onSearchOverlayChange,
  onOpenAllSearch,
  isComparerContext,
  onAddToComparer,
}) => {
  const [filteredShops, setFilteredShops] = useState({});
  const [viewMode, setViewMode] = useState<'brands' | 'categories' | 'around-me' | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [timePreference, setTimePreference] = useState<'single' | 'range'>('single');
  const [selectedTime, setSelectedTime] = useState<string>('12:00');
  const [selectedHour, setSelectedHour] = useState<number>(12);
  const [timeRangeStart, setTimeRangeStart] = useState<string>('09:00');
  const [timeRangeEnd, setTimeRangeEnd] = useState<string>('17:00');
  const [timeRangeStartHour, setTimeRangeStartHour] = useState<number>(9);
  const [timeRangeEndHour, setTimeRangeEndHour] = useState<number>(17);
  const [showPreferencesModal, setShowPreferencesModal] = useState<boolean>(false);
  const [viewType, setViewType] = useState<'table' | 'card'>('table');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    eventLogger('location_tab', { location: `Location Comparer`, tab: viewMode });
  }, [viewMode]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Note: Search overlay state is now handled by AllSearch component
  const { setUpDate, setUpAllDates, getDayCapacities } = useDateHandler();

  // Helper function to convert capacities to dateTime format (local helper, doesn't use state)
  const convertCapacities = (capacities: any): { [dateTime: string]: number } => {
    if (!capacities || typeof capacities !== 'object') return {};

    const result: { [dateTime: string]: number } = {};
    Object.keys(capacities).forEach((dateStr) => {
      const converted = setUpDate(dateStr);
      result[converted] = capacities[dateStr];
    });
    return result;
  };

  // Helper function to get capacity for a specific day and time
  const getCapacityForDateTime = (
    capacities: any,
    day: string,
    hour: number,
    latitude?: number,
    longitude?: number
  ): number | null => {
    if (!capacities || typeof capacities !== 'object') return null;

    const convertedDateTimes = convertCapacities(capacities);
    const timeString = `${String(hour).padStart(2, '0')}:00:00`;
    const dateTimeString = `${day} ${timeString}`;
    return convertedDateTimes[dateTimeString] ?? null;
  };

  // Helper function to get average capacity for a time range
  const getAverageCapacityForTimeRange = (
    capacities: any,
    day: string,
    startHour: number,
    endHour: number,
    latitude?: number,
    longitude?: number
  ): number | null => {
    if (!capacities || typeof capacities !== 'object') return null;

    const convertedDateTimes = convertCapacities(capacities);
    const capacitiesInRange: number[] = [];

    // Iterate through all dateTimes and find those matching the day and within time range
    Object.entries(convertedDateTimes).forEach(([dateTime, capacity]) => {
      if (!dateTime.startsWith(day + ' ')) return;

      const timePart = dateTime.split(' ')[1]; // Get "HH:MM:SS"
      const hour = parseInt(timePart.split(':')[0]);

      if (hour >= startHour && hour <= endHour) {
        const cap = Number(capacity);
        if (!isNaN(cap) && cap > 0) {
          capacitiesInRange.push(cap);
        }
      }
    });

    if (capacitiesInRange.length === 0) return null;

    // Calculate average
    const sum = capacitiesInRange.reduce((acc, val) => acc + val, 0);
    return Math.round(sum / capacitiesInRange.length);
  };

  // Get closest locations for a brand (uses App userLocation)
  const getBrandLocations = useMemo(() => {
    return (brandName: Brand) => {
      if (!allLocations) return [];

      const locations: Array<{ id: string; name: string; distance?: number; locationData: Location }> = [];
      const activeUserLocation = userLocation ? [userLocation[0], userLocation[1]] : null;
      // Normalize brand name for search - uses shared utility function
      const brandLower = normalizeBrandName(brandName);

      // Search through locations
      for (const [locationId, locationData] of Object.entries(allLocations)) {
        if (!locationData || typeof locationData !== 'object') continue;

        const location = locationData as Location;
        const locationName = location.location || locationId;
        const locationNameLower = locationName.toLowerCase();

        // Check if location matches brand
        if (locationNameLower.includes(brandLower) && location.latitude && location.longitude) {
          let distance: number | undefined;

          if (activeUserLocation) {
            distance = calculateDistance(
              activeUserLocation[0],
              activeUserLocation[1],
              location.latitude,
              location.longitude
            );
          }

          locations.push({
            id: locationId,
            name: locationName,
            distance,
            locationData: location,
          });
        }
      }

      // Search through shops (skip for location-only brands like Westfield)
      if (!LOCATION_ONLY_BRANDS.includes(brandName)) {
        for (const shopOption of shops) {
          if (!shopOption || typeof shopOption !== 'string') continue;

          const isShop = shopOption.includes(':::');
          if (!isShop) continue;

          const parts = shopOption.split(' ::: ');
          const locationName = parts[0] || '';
          const shopName = parts[1] || '';
          const shopNameLower = shopName.toLowerCase();

          if (shopNameLower.includes(brandLower)) {
            // Find location data for coordinates
            let locationData: Location | undefined;
            let distance: number | undefined;

            for (const [locationId, locData] of Object.entries(allLocations || {})) {
              if (locData && typeof locData === 'object') {
                const loc = locData as Location;
                if ((loc.location || locationId).toLowerCase() === locationName.toLowerCase()) {
                  locationData = loc;
                  if (loc.latitude && loc.longitude && activeUserLocation) {
                    distance = calculateDistance(
                      activeUserLocation[0],
                      activeUserLocation[1],
                      loc.latitude,
                      loc.longitude
                    );
                  }
                  break;
                }
              }
            }

            locations.push({
              id: shopOption,
              name: shopName,
              distance,
              locationData: locationData,
            });
          }
        }
      }

      // Sort by distance if available, otherwise alphabetically
      locations.sort((a, b) => {
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        if (a.distance !== undefined) return -1;
        if (b.distance !== undefined) return 1;
        return a.name.localeCompare(b.name);
      });

      return locations.slice(0, 5); // Return top 5
    };
  }, [allLocations, shops, userLocation]);

  // Get closest supermarkets
  const getSupermarketLocations = useMemo(() => {
    return () => {
      if (!allLocations) return [];

      const locations: Array<{ id: string; name: string; distance?: number; locationData: Location }> = [];
      const activeUserLocation = userLocation ? [userLocation[0], userLocation[1]] : null;

      // Search through locations
      for (const [locationId, locationData] of Object.entries(allLocations)) {
        if (!locationData || typeof locationData !== 'object') continue;

        const location = locationData as Location;
        const locationName = location.location || locationId;
        const locationNameLower = locationName.toLowerCase();

        const isSupermarket = SUPERMARKET_BRANDS.some((brand) => locationNameLower.includes(brand.toLowerCase()));

        if (isSupermarket && location.latitude && location.longitude) {
          let distance: number | undefined;

          if (activeUserLocation) {
            distance = calculateDistance(
              activeUserLocation[0],
              activeUserLocation[1],
              location.latitude,
              location.longitude
            );
          }

          locations.push({
            id: locationId,
            name: locationName,
            distance,
            locationData: location,
          });
        }
      }

      // Search through shops
      for (const shopOption of shops) {
        if (!shopOption || typeof shopOption !== 'string') continue;

        const isShop = shopOption.includes(':::');
        if (!isShop) continue;

        const parts = shopOption.split(' ::: ');
        const locationName = parts[0] || '';
        const shopName = parts[1] || '';
        const shopNameLower = shopName.toLowerCase();

        const isSupermarket = SUPERMARKET_BRANDS.some((brand) => shopNameLower.includes(brand.toLowerCase()));

        if (isSupermarket) {
          let locationData: Location | undefined;
          let distance: number | undefined;

          for (const [locationId, locData] of Object.entries(allLocations || {})) {
            if (locData && typeof locData === 'object') {
              const loc = locData as Location;
              if ((loc.location || locationId).toLowerCase() === locationName.toLowerCase()) {
                locationData = loc;
                if (loc.latitude && loc.longitude && activeUserLocation) {
                  distance = calculateDistance(
                    activeUserLocation[0],
                    activeUserLocation[1],
                    loc.latitude,
                    loc.longitude
                  );
                }
                break;
              }
            }
          }

          locations.push({
            id: shopOption,
            name: shopName,
            distance,
            locationData: locationData,
          });
        }
      }

      // Sort by distance if available, otherwise alphabetically
      locations.sort((a, b) => {
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        if (a.distance !== undefined) return -1;
        if (b.distance !== undefined) return 1;
        return a.name.localeCompare(b.name);
      });

      return locations.slice(0, 5); // Return top 5
    };
  }, [allLocations, shops, userLocation]);

  // Auto-populate locations when brand/category is selected
  const handleBrandSelect = async (brandName: Brand) => {
    const locations = getBrandLocations(brandName);
    await autoPopulateLocations(locations);
    setViewMode(null);
  };

  const handleSupermarketSelect = async () => {
    const locations = getSupermarketLocations();
    await autoPopulateLocations(locations);
    setViewMode(null);
  };

  // Get closest electronics stores (JB Hi-Fi and Harvey Norman)
  const getElectronicsLocations = useMemo(() => {
    return () => {
      if (!allLocations) return [];

      const locations: Array<{ id: string; name: string; distance?: number; locationData: Location }> = [];
      const activeUserLocation = userLocation ? [userLocation[0], userLocation[1]] : null;

      // Search through locations
      for (const [locationId, locationData] of Object.entries(allLocations)) {
        if (!locationData || typeof locationData !== 'object') continue;

        const location = locationData as Location;
        const locationName = location.location || locationId;
        const locationNameLower = locationName.toLowerCase();

        const isElectronics = ELECTRONICS_BRANDS.some((brand) => {
          return matchesBrand(locationName, brand);
        });

        if (isElectronics && location.latitude && location.longitude) {
          let distance: number | undefined;

          if (activeUserLocation) {
            distance = calculateDistance(
              activeUserLocation[0],
              activeUserLocation[1],
              location.latitude,
              location.longitude
            );
          }

          locations.push({
            id: locationId,
            name: locationName,
            distance,
            locationData: location,
          });
        }
      }

      // Search through shops
      for (const shopOption of shops) {
        if (!shopOption || typeof shopOption !== 'string') continue;

        const isShop = shopOption.includes(':::');
        if (!isShop) continue;

        const parts = shopOption.split(' ::: ');
        const locationName = parts[0] || '';
        const shopName = parts[1] || '';
        const shopNameLower = shopName.toLowerCase();

        const isElectronics = ELECTRONICS_BRANDS.some((brand) => {
          return matchesBrand(shopName, brand);
        });

        if (isElectronics) {
          let locationData: Location | undefined;
          let distance: number | undefined;

          for (const [locationId, locData] of Object.entries(allLocations || {})) {
            if (locData && typeof locData === 'object') {
              const loc = locData as Location;
              if ((loc.location || locationId).toLowerCase() === locationName.toLowerCase()) {
                locationData = loc;
                if (loc.latitude && loc.longitude && activeUserLocation) {
                  distance = calculateDistance(
                    activeUserLocation[0],
                    activeUserLocation[1],
                    loc.latitude,
                    loc.longitude
                  );
                }
                break;
              }
            }
          }

          locations.push({
            id: shopOption,
            name: shopName,
            distance,
            locationData: locationData,
          });
        }
      }

      // Sort by distance if available, otherwise alphabetically
      locations.sort((a, b) => {
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        if (a.distance !== undefined) return -1;
        if (b.distance !== undefined) return 1;
        return a.name.localeCompare(b.name);
      });

      return locations.slice(0, 5); // Return top 5
    };
  }, [allLocations, shops, userLocation]);

  const handleElectronicsSelect = async () => {
    const locations = getElectronicsLocations();
    await autoPopulateLocations(locations);
    setViewMode(null);
  };

  // Get closest beaches
  const getBeachesLocations = useMemo(() => {
    return () => {
      if (!allLocations) return [];

      const locations: Array<{ id: string; name: string; distance?: number; locationData: Location }> = [];
      const activeUserLocation = userLocation ? [userLocation[0], userLocation[1]] : null;

      // Search through locations
      for (const [locationId, locationData] of Object.entries(allLocations)) {
        if (!locationData || typeof locationData !== 'object') continue;

        const location = locationData as Location;
        const locationName = location.location || locationId;

        const isBeach = location.category === 'Beaches';

        if (isBeach && location.latitude && location.longitude) {
          let distance: number | undefined;

          if (activeUserLocation) {
            distance = calculateDistance(
              activeUserLocation[0],
              activeUserLocation[1],
              location.latitude,
              location.longitude
            );
          }

          locations.push({
            id: locationId,
            name: locationName,
            distance,
            locationData: location,
          });
        }
      }

      // Sort by distance if available, otherwise alphabetically
      locations.sort((a, b) => {
        if (a.distance !== undefined && b.distance !== undefined) {
          return a.distance - b.distance;
        }
        if (a.distance !== undefined) return -1;
        if (b.distance !== undefined) return 1;
        return a.name.localeCompare(b.name);
      });

      return locations.slice(0, 5); // Return top 5
    };
  }, [allLocations, userLocation]);

  const handleBeachesSelect = async () => {
    const locations = getBeachesLocations();
    await autoPopulateLocations(locations);
    setViewMode(null);
  };

  // Get busyness color (same as crowd levels)
  const getBusynessColor = (capacity: number | null | undefined): string => {
    if (capacity === undefined || capacity === null || capacity === 0) return '#64748b'; // Gray for closed/unknown
    if (capacity < 30) return '#10b981'; // Green - Very Quiet
    if (capacity < 60) return '#f59e0b'; // Orange - Moderate
    if (capacity < 80) return '#fb923c'; // Red-Orange - Busy
    return '#ef4444'; // Red - Very Busy
  };

  // Color palette for comparison chart (distinct colors for up to 5 locations)

  const autoPopulateLocations = async (
    locations: Array<{ id: string; name: string; distance?: number; locationData: Location }>,
    clearExisting: boolean = true
  ) => {
    // Clear existing results if clearExisting is true (default)
    const newShops: Record<string, any> = clearExisting ? {} : { ...filteredShops };

    for (const loc of locations) {
      if (Object.keys(newShops).length >= 5) break;
      if (loc.id in newShops) continue;

      try {
        let shopData = null;
        if (loc.id.includes(':::')) {
          const info = loc.id.split(' ::: ');
          shopData = await getData(`/locations/${info[0]}/shops/${info[1]}`);
        } else {
          shopData = await getData(`/locations/${loc.id}`);
        }

        if (shopData) {
          const currentDateTime = getLocalCurrentDateTime(shopData.latitude, shopData.longitude);
          const convertedDateTimes = setUpAllDates(shopData.capacities);

          // Use selected day/time if available, otherwise use current
          let capacity: number | null = null;
          let dayData: [string, number][] = [];

          if (selectedDay) {
            if (timePreference === 'range') {
              capacity = getAverageCapacityForTimeRange(
                shopData.capacities,
                selectedDay,
                timeRangeStartHour,
                timeRangeEndHour,
                shopData.latitude,
                shopData.longitude
              );
            } else {
              capacity = getCapacityForDateTime(
                shopData.capacities,
                selectedDay,
                selectedHour,
                shopData.latitude,
                shopData.longitude
              );
            }
            dayData = getDayCapacities(selectedDay, convertedDateTimes);
          } else {
            const convertedCurrentDateTime = setUpDate(currentDateTime.dateTime);
            capacity = convertedDateTimes[convertedCurrentDateTime] ?? null;
            dayData = getDayCapacities(currentDateTime.day, convertedDateTimes);
          }

          // Store distance if available
          newShops[loc.id] = {
            ...shopData,
            currentCapacity: capacity,
            todayData: dayData,
            currentDateTime,
            distance: loc.distance,
          };
        }
      } catch (error) {
        console.error(`Error fetching data for ${loc.id}:`, error);
      }
    }

    // Sort by busy status (lowest capacity first), then convert back to object
    const sortedEntries = Object.entries(newShops).sort(([, a], [, b]) => {
      const capacityA = a.currentCapacity ?? 999; // Treat null/undefined as very high (bottom of list)
      const capacityB = b.currentCapacity ?? 999;
      return capacityA - capacityB; // Lowest first
    });

    const sortedShops = Object.fromEntries(sortedEntries);
    setFilteredShops(sortedShops);
  };

  const handleSearchSubmit = useCallback(async (id: string) => {
    setFilteredShops((prevFilteredShops) => {
      if (id in prevFilteredShops || Object.keys(prevFilteredShops).length >= 5) {
        return prevFilteredShops;
      }
      return prevFilteredShops;
    });

    // Check again after state update (async check)
    const currentFilteredShops = filteredShops;
    if (id in currentFilteredShops || Object.keys(currentFilteredShops).length >= 5) {
      return;
    }

      let shopData = null;
      if (id.includes(':::')) {
        const info = id.split(' ::: ');
        shopData = await getData(`/locations/${info[0]}/shops/${info[1]}`);
      } else {
        shopData = await getData(`/locations/${id}`);
      }
      const currentDateTime = getLocalCurrentDateTime(shopData.latitude, shopData.longitude);
      const convertedDateTimes = setUpAllDates(shopData.capacities);

      // Use selected day/time if available, otherwise use current
      let capacity: number | null = null;
      let dayData: [string, number][] = [];

      if (selectedDay) {
        if (timePreference === 'range') {
          capacity = getAverageCapacityForTimeRange(
            shopData.capacities,
            selectedDay,
            timeRangeStartHour,
            timeRangeEndHour,
            shopData.latitude,
            shopData.longitude
          );
        } else {
          capacity = getCapacityForDateTime(
            shopData.capacities,
            selectedDay,
            selectedHour,
            shopData.latitude,
            shopData.longitude
          );
        }
        dayData = getDayCapacities(selectedDay, convertedDateTimes);
      } else {
        const convertedCurrentDateTime = setUpDate(currentDateTime.dateTime);
        capacity = convertedDateTimes[convertedCurrentDateTime] ?? null;
        dayData = getDayCapacities(currentDateTime.day, convertedDateTimes);
      }

    setFilteredShops((prev) => {
      // Double-check we haven't exceeded limit or already added
      if (id in prev || Object.keys(prev).length >= 5) {
        return prev;
      }
      return {
        ...prev,
        [id]: { ...shopData, currentCapacity: capacity, todayData: dayData, currentDateTime },
      };
    });
  }, [filteredShops, selectedDay, timePreference, timeRangeStartHour, timeRangeEndHour, selectedHour]);

  // Expose handleSearchSubmit to parent via callback
  useEffect(() => {
    if (onAddToComparer) {
      onAddToComparer(handleSearchSubmit);
    }
    // Cleanup: clear callback when component unmounts
    return () => {
      if (onAddToComparer) {
        onAddToComparer(null);
    }
  };
  }, [handleSearchSubmit, onAddToComparer]);

  const handleShopClick = (name, details) => {
    handleSearchSubmission(name, details);
  };

  const handleRemoveShop = (shopId) => {
    const updatedShops = { ...filteredShops };
    delete updatedShops[shopId];
    setFilteredShops(updatedShops);
  };

  // Recalculate capacities when day/time selection changes
  useEffect(() => {
    if (Object.keys(filteredShops).length === 0) return;

    const recalculateCapacities = async () => {
      const updatedShops = { ...filteredShops };

      for (const shopId of Object.keys(updatedShops)) {
        const shop = updatedShops[shopId];
        if (!shop || !shop.capacities) continue;

        const convertedDateTimes = setUpAllDates(shop.capacities);
        let capacity: number | null = null;
        let dayData: [string, number][] = [];

        if (selectedDay) {
          if (timePreference === 'range') {
            capacity = getAverageCapacityForTimeRange(
              shop.capacities,
              selectedDay,
              timeRangeStartHour,
              timeRangeEndHour,
              shop.latitude,
              shop.longitude
            );
          } else {
            capacity = getCapacityForDateTime(
              shop.capacities,
              selectedDay,
              selectedHour,
              shop.latitude,
              shop.longitude
            );
          }
          dayData = getDayCapacities(selectedDay, convertedDateTimes);
        } else {
          // Use current time
          const currentDateTime = getLocalCurrentDateTime(shop.latitude || -33.86, shop.longitude || 151.21);
          const convertedCurrentDateTime = setUpDate(currentDateTime.dateTime);
          capacity = convertedDateTimes[convertedCurrentDateTime] ?? null;
          dayData = getDayCapacities(currentDateTime.day, convertedDateTimes);
        }

        updatedShops[shopId] = {
          ...shop,
          currentCapacity: capacity,
          todayData: dayData,
        };
      }

      // Re-sort by capacity
      const sortedEntries = Object.entries(updatedShops).sort(([, a], [, b]) => {
        const capacityA = (a as any).currentCapacity ?? 999;
        const capacityB = (b as any).currentCapacity ?? 999;
        return capacityA - capacityB;
      });

      setFilteredShops(Object.fromEntries(sortedEntries));
    };

    recalculateCapacities();
  }, [selectedDay, timePreference, selectedHour, timeRangeStartHour, timeRangeEndHour]);

  // Note: Custom search now opens AllSearch component via onOpenAllSearch prop

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay fullscreen"
      onClick={handleClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="location-modal fullscreen"
        onClick={(e) => e.stopPropagation()}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        {/* Header */}
        <div className="modal-header fullscreen" style={{ flexShrink: 0 }}>
          <div className="header-left">
            <h1 className="location-title">Compare Locations</h1>
          </div>
          <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {/* <button
              onClick={() => setShowPreferencesModal(true)}
              className="icon-action-button"
              title="Comparison Settings"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
              }}
            >
              <Settings size={20} />
            </button> */}
            <button onClick={handleClose} className="icon-action-button close" title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <div className="comparer-navigation" style={{ flexShrink: 0 }}>
          <button
            onClick={() => {
              setViewMode(viewMode === 'brands' ? null : 'brands');
            }}
            className={`comparer-nav-button ${viewMode === 'brands' ? 'active' : ''}`}
          >
            <ShoppingBag size={18} />
            <span>Brands</span>
          </button>
          <button
            onClick={() => {
              setViewMode(viewMode === 'categories' ? null : 'categories');
            }}
            className={`comparer-nav-button ${viewMode === 'categories' ? 'active' : ''}`}
          >
            <List size={18} />
            <span>Categories</span>
          </button>
          <button
            onClick={() => {
              if (onOpenAllSearch) {
                onOpenAllSearch();
              }
            }}
            className="comparer-nav-button"
          >
            <Search size={18} />
            <span>Custom Search</span>
          </button>
          {/* CHANGE: This view locates the user when it's opened if it can. No point having a view for this, since it's confusing UX. <button
            onClick={() => {
              if (viewMode === 'around-me') {
                setViewMode(null);
              } else {
                setViewMode('around-me');
                if (!userLocation && onRequestLocation) {
                  onRequestLocation();
                }
              }
            }}
            className={`comparer-nav-button ${viewMode === 'around-me' ? 'active' : ''}`}
            disabled={isLocating}
          >
            {isLocating ? <div className="location-spinner-small"></div> : <Navigation size={18} />}
            <span>Around Me</span>
          </button> */}
        </div>

        {/* Content - Scrollable */}
        <div
          className={`modal-content fullscreen ${
            viewMode === 'brands' || viewMode === 'categories' ? 'comparer-grid-view' : ''
          }`}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            padding: viewMode === 'brands' || viewMode === 'categories' ? 0 : undefined,
          }}
        >
          {/* Brands View - Compact grid like SearchDropdown */}
          {viewMode === 'brands' && (
            <div className="brand-filters-view">
              <div className="recent-searches-header">
                <ShoppingBag size={16} className="recent-searches-header-icon" />
                <span className="recent-searches-header-text">Browse Brands</span>
              </div>
              <div className="categories-grid">
                {BRANDS.map((brand) => {
                  const IconComponent = BRAND_ICONS[brand];
                  return (
                    <button key={brand} onClick={() => handleBrandSelect(brand)} className="category-item">
                      <IconComponent size={20} className="category-icon" />
                      <span className="category-name">{brand}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Categories View - Compact grid like SearchDropdown */}
          {viewMode === 'categories' && (
            <div className="categories-view">
              <div className="recent-searches-header">
                <List size={16} className="recent-searches-header-icon" />
                <span className="recent-searches-header-text">Categories</span>
              </div>
              <div className="categories-grid">
                <button onClick={handleSupermarketSelect} className="category-item">
                  <Store size={20} className="category-icon" />
                  <span className="category-name">Supermarket</span>
                </button>
                <button onClick={handleElectronicsSelect} className="category-item">
                  <Music size={20} className="category-icon" />
                  <span className="category-name">Electronics</span>
                </button>
                <button onClick={handleBeachesSelect} className="category-item">
                  <Waves size={20} className="category-icon" />
                  <span className="category-name">Beaches</span>
                </button>
              </div>
            </div>
          )}

          {/* Around Me View */}
          {viewMode === 'around-me' && (
            <div style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Navigation size={20} />
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600 }}>Locations Near You</h3>
              </div>
              {isLocating ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div
                    className="location-spinner-small"
                    style={{ margin: '0 auto', width: '24px', height: '24px' }}
                  ></div>
                  <p>Getting your location...</p>
                </div>
              ) : userLocation ? (
                <p style={{ color: '#6b7280' }}>
                  Location enabled. Select a brand or category to find nearby locations.
                </p>
              ) : (
                <p style={{ color: '#6b7280' }}>Enable location to find nearby locations.</p>
              )}
            </div>
          )}

          {/* Custom Search now opens AllSearch component via onOpenAllSearch prop */}

          {Object.keys(filteredShops).length > 0 ? (
            <React.Fragment>
              {/* Consistent Header for Both Views */}
              <div className="comparison-header compact">
                <div className="comparison-header-row">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 }}>
                    <h2 className="comparison-title compact">
                      <Store size={14} />
                      <span>
                        Comparing {Object.keys(filteredShops).length} Location
                        {Object.keys(filteredShops).length !== 1 ? 's' : ''}
                      </span>
                    </h2>
                    {/* Time/Time Range Display */}
                    {/* <div
                      style={{
                        fontSize: '0.8125rem',
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginLeft: '1.75rem',
                      }}
                    >
                      <Clock size={12} />
                      <span>
                        {selectedDay ? `${selectedDay}, ` : ''}
                        {timePreference === 'single'
                          ? `${selectedHour}:00`
                          : `${timeRangeStartHour}:00 - ${timeRangeEndHour}:00`}
                      </span>
                    </div> */}
                  </div>
                  {/* View Toggle */}
                  <div className="view-toggle-container">
                    <button
                      onClick={() => setViewType('table')}
                      className={`view-toggle-button ${viewType === 'table' ? 'active' : ''}`}
                      title="Table View - Compact Comparison"
                    >
                      <Table2 size={16} />
                    </button>
                    <button
                      onClick={() => setViewType('card')}
                      className={`view-toggle-button ${viewType === 'card' ? 'active' : ''}`}
                      title="Card View - Detailed Information"
                    >
                      <Grid3x3 size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Content - Changes Based on View Type */}
              {viewType === 'table' ? (
                <React.Fragment>
                  {/* Table View - Compact Comparison */}
                  <div className="shops-table-wrapper">
                    <div className="shops-table-container">
                      <table className="shops-table">
                        <thead>
                          <tr>
                            <th>Shop Name</th>
                            <th>Status</th>
                            <th>Distance</th>
                            <th>Remove</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.keys(filteredShops).map((shopId) => {
                            const shop = filteredShops[shopId];
                            if (!shop) return null;
                            const currentCapacity = shop.currentCapacity;
                            const todayData = shop.todayData;
                            const isOpen = currentCapacity !== null && currentCapacity > 0;

                            return (
                              <tr
                                key={shopId}
                                className="shop-row clickable-row"
                                onClick={() => handleShopClick(shopId, filteredShops[shopId])}
                              >
                                <td>
                                  <div className="shop-name-cell">
                                    <strong>{shopId}</strong>
                                    <span className="shop-click-hint">Click to view details</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="shop-status-comparer">
                                    {currentCapacity !== null && currentCapacity !== undefined ? (
                                      <div
                                        className={`status-badge ${isOpen ? 'open' : 'closed'}`}
                                        style={{
                                          backgroundColor: isOpen ? getBusynessColor(currentCapacity) : '#64748b',
                                          color: 'white',
                                          padding: '0.5rem 0.75rem',
                                          borderRadius: '8px',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '0.5rem',
                                          fontWeight: 500,
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        {isOpen ? (
                                          <>
                                            <span
                                              className="status-dot"
                                              style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: 'white',
                                              }}
                                            ></span>
                                            Open • {currentCapacity}% busy
                                          </>
                                        ) : (
                                          <>
                                            <span
                                              className="status-dot"
                                              style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: 'white',
                                              }}
                                            ></span>
                                            Closed
                                          </>
                                        )}
                                      </div>
                                    ) : (
                                      <span
                                        className="status-unknown"
                                        style={{
                                          color: '#64748b',
                                          padding: '0.5rem 0.75rem',
                                          borderRadius: '8px',
                                          backgroundColor: '#f1f5f9',
                                          display: 'inline-block',
                                          fontSize: '0.875rem',
                                        }}
                                      >
                                        Unknown
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.5rem',
                                      color: '#64748b',
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    {shop.distance !== undefined && shop.distance !== null ? (
                                      <>
                                        <Navigation size={14} />
                                        <span>{formatDistance(shop.distance)}</span>
                                      </>
                                    ) : (
                                      <span style={{ color: '#9ca3af' }}>—</span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveShop(shopId);
                                    }}
                                    className="icon-action-button close"
                                    title="Remove shop"
                                  >
                                    <X size={20} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Message when 5 locations are added */}
                  {Object.keys(filteredShops).length === 5 && (
                    <div
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        backgroundColor: '#f0f9ff',
                        border: '1px solid #bae6fd',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem',
                        color: '#0369a1',
                      }}
                    >
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#0ea5e9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>5</span>
                      </div>
                      <span style={{ fontWeight: 500 }}>
                        Maximum locations reached. Remove a location using the
                        <X
                          size={14}
                          style={{
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            margin: '0 0.25rem',
                            color: '#0369a1',
                          }}
                        />
                        button to add a custom location.
                      </span>
                    </div>
                  )}
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {/* Card View - Detailed Information */}
                  <div className="comparison-cards-container">
                    {/* Comparison Cards Grid */}
                    <div className="comparison-cards-grid">
                      {Object.keys(filteredShops).map((shopId) => {
                        const shop = filteredShops[shopId];
                        if (!shop) return null;
                        const currentCapacity = shop.currentCapacity;
                        const todayData = shop.todayData;
                        const isOpen = currentCapacity !== null && currentCapacity > 0;
                        const busynessColor = isOpen ? getBusynessColor(currentCapacity) : '#64748b';

                        return (
                          <div
                            key={shopId}
                            className="comparison-card"
                            onClick={() => handleShopClick(shopId, filteredShops[shopId])}
                          >
                            {/* Card Header */}
                            <div className="comparison-card-header">
                              <div className="comparison-card-title-section">
                                <h3 className="comparison-card-title">{shopId}</h3>
                                {shop.distance !== undefined && shop.distance !== null && (
                                  <div className="comparison-card-distance">
                                    <Navigation size={14} />
                                    <span>{formatDistance(shop.distance)}</span>
                                  </div>
                                )}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveShop(shopId);
                                }}
                                className="comparison-card-remove"
                                title="Remove from comparison"
                              >
                                <X size={18} />
                              </button>
                            </div>

                            {/* Status Badge */}
                            <div className="comparison-card-status">
                              {currentCapacity !== null && currentCapacity !== undefined ? (
                                <div
                                  className="comparison-status-badge"
                                  style={{
                                    backgroundColor: busynessColor,
                                    color: 'white',
                                  }}
                                >
                                  <div className="status-indicator">
                                    <span className="status-dot-active"></span>
                                    {isOpen ? (
                                      <span className="status-text" style={{ color: 'white' }}>
                                        Open • {currentCapacity}% Busy
                                      </span>
                                    ) : (
                                      <span className="status-text" style={{ color: 'white' }}>
                                        Closed
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="comparison-status-badge status-unknown">
                                  <span className="status-text">Status Unknown</span>
                                </div>
                              )}
                            </div>

                            {/* Busyness Progress Bar */}
                            {currentCapacity !== null && currentCapacity !== undefined && isOpen && (
                              <div className="comparison-busyness-bar">
                                <div className="busyness-bar-label">
                                  <span>Current Busyness</span>
                                  <span className="busyness-percentage">{currentCapacity}%</span>
                                </div>
                                <div className="busyness-bar-container">
                                  <div
                                    className="busyness-bar-fill"
                                    style={{
                                      width: `${currentCapacity}%`,
                                      backgroundColor: busynessColor,
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Crowd Levels Chart */}
                            <div className="comparison-chart-section">
                              <div className="comparison-chart-header">
                                <span className="comparison-chart-label">Today's Crowd Levels</span>
                              </div>
                              <div className="comparison-chart-wrapper">
                                <AreaLineChart
                                  labels={todayData.map((item) => item[0].split(' ')[1].slice(0, -3))}
                                  dataPoints={todayData.map((item) => Number(item[1]))}
                                  showXScale={true}
                                  showYScale={false}
                                  currentTimeIndex={shop.currentDateTime.dateTime.split('T')[1].slice(0, -3)}
                                />
                              </div>
                            </div>

                            {/* Card Footer */}
                            <div className="comparison-card-footer">
                              <span className="comparison-card-hint">Tap to view details →</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </React.Fragment>
              )}
            </React.Fragment>
          ) : (
            <div className="comparison-empty-state">
              <div className="empty-state-icon">
                <Store size={64} />
              </div>
              <h3 className="empty-state-title">Start Comparing Locations</h3>
              <p className="empty-state-description">
                Select a brand, category, or use custom search to add locations for comparison
              </p>
            </div>
          )}
        </div>

        {/* Preferences Modal */}
        {showPreferencesModal &&
          createPortal(
            <ComparisonPreferencesModal
              isOpen={showPreferencesModal}
              onClose={() => setShowPreferencesModal(false)}
              selectedDay={selectedDay}
              timePreference={timePreference}
              selectedHour={selectedHour}
              timeRangeStartHour={timeRangeStartHour}
              timeRangeEndHour={timeRangeEndHour}
              onSave={(prefs) => {
                setSelectedDay(prefs.selectedDay);
                setTimePreference(prefs.timePreference);
                setSelectedHour(prefs.selectedHour);
                setTimeRangeStartHour(prefs.timeRangeStartHour);
                setTimeRangeEndHour(prefs.timeRangeEndHour);
                setShowPreferencesModal(false);
              }}
            />,
            document.body
          )}
      </motion.div>
    </motion.div>
  );
};

// Comparison Preferences Modal Component
const ComparisonPreferencesModal = ({
  isOpen,
  onClose,
  selectedDay,
  timePreference,
  selectedHour,
  timeRangeStartHour,
  timeRangeEndHour,
  onSave,
}) => {
  const [tempDay, setTempDay] = useState<string | null>(selectedDay);
  const [tempTimePreference, setTempTimePreference] = useState<'single' | 'range'>(timePreference);
  const [tempSelectedHour, setTempSelectedHour] = useState<number>(selectedHour);
  const [tempTimeRangeStartHour, setTempTimeRangeStartHour] = useState<number>(timeRangeStartHour);
  const [tempTimeRangeEndHour, setTempTimeRangeEndHour] = useState<number>(timeRangeEndHour);

  useEffect(() => {
    if (isOpen) {
      setTempDay(selectedDay);
      setTempTimePreference(timePreference);
      setTempSelectedHour(selectedHour);
      setTempTimeRangeStartHour(timeRangeStartHour);
      setTempTimeRangeEndHour(timeRangeEndHour);
    }
  }, [isOpen, selectedDay, timePreference, selectedHour, timeRangeStartHour, timeRangeEndHour]);

  const handleSave = () => {
    onSave({
      selectedDay: tempDay,
      timePreference: tempTimePreference,
      selectedHour: tempSelectedHour,
      timeRangeStartHour: tempTimeRangeStartHour,
      timeRangeEndHour: tempTimeRangeEndHour,
    });
  };

  const handleReset = () => {
    // Get current time
    const now = new Date();
    const currentHour = now.getHours();

    // Reset to current time settings
    const resetPrefs = {
      selectedDay: null, // Current day
      timePreference: 'single' as const,
      selectedHour: currentHour,
      timeRangeStartHour: currentHour,
      timeRangeEndHour: currentHour,
    };

    // Save the reset preferences and close modal
    onSave(resetPrefs);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="preferences-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="preferences-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="preferences-modal-header">
          <div className="preferences-modal-title">
            <Settings size={24} />
            <div>
              <h3>Comparison Settings</h3>
              <p>Set the day and time for comparing locations</p>
            </div>
          </div>
          <button onClick={handleSave} className="save-preferences-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
              <polyline points="17,21 17,13 7,13 7,21"></polyline>
              <polyline points="7,3 7,8 15,8"></polyline>
            </svg>
            <span>Save</span>
          </button>
        </div>

        {/* Modal Content */}
        <div className="preferences-modal-content">
          {/* Custom Preferences Card */}
          <div className="preferences-filters-card">
            <h4>Custom Preferences</h4>

            {/* Day Selection */}
            <div className="filter-group">
              <label className="filter-label">
                <Calendar size={16} />
                Day Selection
              </label>
              <div className="filter-options">
                <button
                  className={`filter-option ${tempDay === null ? 'active' : ''}`}
                  onClick={() => setTempDay(null)}
                >
                  Current Day
                </button>
                <button
                  className={`filter-option ${tempDay !== null ? 'active' : ''}`}
                  onClick={() => {
                    const currentDayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
                    setTempDay(DAYSOFWEEK[currentDayIndex]);
                  }}
                >
                  Select Day
                </button>
              </div>

              {/* Day Selector */}
              {tempDay !== null && (
                <div style={{ marginTop: '1rem' }}>
                  <DayOfWeekSelector
                    currentDay={tempDay || DAYSOFWEEK[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]}
                    onSelect={(day) => setTempDay(day)}
                  />
                </div>
              )}
            </div>

            {/* Time Preference */}
            <div className="filter-group">
              <label className="filter-label">
                <Clock size={16} />
                Time Range
              </label>
              <div className="filter-options">
                <button
                  className={`filter-option ${tempTimePreference === 'single' ? 'active' : ''}`}
                  onClick={() => setTempTimePreference('single')}
                >
                  Single Time
                </button>
                <button
                  className={`filter-option ${tempTimePreference === 'range' ? 'active' : ''}`}
                  onClick={() => setTempTimePreference('range')}
                >
                  Time Range
                </button>
              </div>

              {/* Single Time Input */}
              {tempTimePreference === 'single' && (
                <div style={{ marginTop: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: '#64748b' }}>
                    Select Hour
                  </label>
                  <select
                    value={tempSelectedHour}
                    onChange={(e) => setTempSelectedHour(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      background: 'white',
                    }}
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {i}:00
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Time Range with Presets */}
              {tempTimePreference === 'range' && (
                <div style={{ marginTop: '1rem' }}>
                  {/* Preset Options */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '0.75rem',
                      marginBottom: '1rem',
                    }}
                  >
                    <button
                      onClick={() => {
                        setTempTimeRangeStartHour(6);
                        setTempTimeRangeEndHour(12);
                      }}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        background: tempTimeRangeStartHour === 6 && tempTimeRangeEndHour === 12 ? '#f3f4f6' : 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (tempTimeRangeStartHour !== 6 || tempTimeRangeEndHour !== 12) {
                          e.currentTarget.style.background = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (tempTimeRangeStartHour !== 6 || tempTimeRangeEndHour !== 12) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Morning</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>6am - 12pm</div>
                    </button>
                    <button
                      onClick={() => {
                        setTempTimeRangeStartHour(12);
                        setTempTimeRangeEndHour(18);
                      }}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        background: tempTimeRangeStartHour === 12 && tempTimeRangeEndHour === 18 ? '#f3f4f6' : 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (tempTimeRangeStartHour !== 12 || tempTimeRangeEndHour !== 18) {
                          e.currentTarget.style.background = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (tempTimeRangeStartHour !== 12 || tempTimeRangeEndHour !== 18) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Afternoon</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>12pm - 6pm</div>
                    </button>
                    <button
                      onClick={() => {
                        setTempTimeRangeStartHour(18);
                        setTempTimeRangeEndHour(23);
                      }}
                      style={{
                        padding: '0.75rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        background: tempTimeRangeStartHour === 18 && tempTimeRangeEndHour === 23 ? '#f3f4f6' : 'white',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        if (tempTimeRangeStartHour !== 18 || tempTimeRangeEndHour !== 23) {
                          e.currentTarget.style.background = '#f9fafb';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (tempTimeRangeStartHour !== 18 || tempTimeRangeEndHour !== 23) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                    >
                      <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Evening</div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>6pm - 11pm</div>
                    </button>
                  </div>

                  {/* Custom Time Range */}
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                    <label
                      style={{
                        display: 'block',
                        marginBottom: '0.75rem',
                        fontSize: '0.875rem',
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      Custom Range
                    </label>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.75rem', color: '#94a3b8' }}
                        >
                          Start Hour
                        </label>
                        <select
                          value={tempTimeRangeStartHour}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (val < tempTimeRangeEndHour) {
                              setTempTimeRangeStartHour(val);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            background: 'white',
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i} disabled={i >= tempTimeRangeEndHour}>
                              {i}:00
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ color: '#94a3b8', marginTop: '1.5rem' }}>to</div>
                      <div style={{ flex: 1 }}>
                        <label
                          style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.75rem', color: '#94a3b8' }}
                        >
                          End Hour
                        </label>
                        <select
                          value={tempTimeRangeEndHour}
                          onChange={(e) => {
                            const val = parseInt(e.target.value);
                            if (val > tempTimeRangeStartHour) {
                              setTempTimeRangeEndHour(val);
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            cursor: 'pointer',
                            background: 'white',
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i} disabled={i <= tempTimeRangeStartHour}>
                              {i}:00
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: '#94a3b8' }}>
                      Average busy level will be calculated for this time range
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Reset Button */}
            <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e7eb' }}>
              <button
                onClick={handleReset}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Reset to Current Time
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LocationComparer2;
